<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Button, Dialog, Dropdown, InputText } from 'primevue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { usePackageTypeStore } from 'shared/packages/stores/packageTypeStore';
import { usePackagingStore } from 'shared/packages/stores/packagingStore';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefreshPackageTypeStore } from '@/packages/commands/refreshPackageTypeStore';
import { useRefreshPackagingStore } from '@/packages/commands/refreshPackagingStore';
import { useMappingTraceStore } from 'shared/products/mapping/stores/mappingTraceStore';
import { useUpdateMappingTrace } from '../api/updateMappingTrace';

defineExpose({ show });

const emit = defineEmits<{
  saved: [id: string];
}>();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const productStore = useProductStore();
const packageTypeStore = usePackageTypeStore();
const packagingStore = usePackagingStore();
const mappingTraceStore = useMappingTraceStore();

const refreshProductStore = useRefereshProductStore();
const refreshPackageTypeStore = useRefreshPackageTypeStore();
const refreshPackagingStore = useRefreshPackagingStore();

const updateMappingTrace = useUpdateMappingTrace();

const visible = ref(false);
const loading = ref(false);
const mappingTraceId = ref<string | null>(null);

const correctedProductId = ref<string | null>(null);
const correctedPackageTypeId = ref<string | null>(null);
const correctedPackagingId = ref<string | null>(null);
const correctionNote = ref('');

const title = computed(() => mappingTraceId.value ? 'Correct mapping trace' : 'Mapping trace');

const productOptions = computed(() => productStore.sortedByName.map((item) => ({ label: item.name, value: item.id })));
const packageTypeOptions = computed(() => packageTypeStore.sortedByName.map((item) => ({ label: item.name, value: item.id })));
const packagingOptions = computed(() => packagingStore.sortedByName.map((item) => ({ label: item.name, value: item.id })));

onMounted(() => {
  refreshProductStore.initialize();
  refreshPackageTypeStore.initialize();
  refreshPackagingStore.initialize();
});

function reset() {
  mappingTraceId.value = null;
  correctedProductId.value = null;
  correctedPackageTypeId.value = null;
  correctedPackagingId.value = null;
  correctionNote.value = '';
}

function show(id?: string) {
  reset();

  if (!id) {
    visible.value = true;
    return;
  }

  const item = mappingTraceStore.getById(id);
  if (!item) {
    return;
  }

  mappingTraceId.value = item.id;
  correctedProductId.value = item.resolvedProductId;
  correctedPackageTypeId.value = item.resolvedPackageTypeId;
  correctedPackagingId.value = item.resolvedPackagingId;
  correctionNote.value = item.correctionNote ?? '';
  visible.value = true;
}

async function save() {
  if (!mappingTraceId.value) return;

  loading.value = true;
  try {
    const response = await updateMappingTrace.execute({
      id: mappingTraceId.value,
      entity: {
        correctedProductId: correctedProductId.value,
        correctedPackageTypeId: correctedPackageTypeId.value,
        correctedPackagingId: correctedPackagingId.value,
        correctionNote: correctionNote.value.trim() || null,
      },
    });

    if (!response.ok) {
      return;
    }

    visible.value = false;
    emit('saved', response.mappingTrace.id);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :header="title" :style="{ width: '40rem' }">
    <div class="form-grid">
      <label>Corrected product</label>
      <Dropdown v-model="correctedProductId" :options="productOptions" optionLabel="label" optionValue="value" showClear filter></Dropdown>

      <label>Corrected package type</label>
      <Dropdown v-model="correctedPackageTypeId" :options="packageTypeOptions" optionLabel="label" optionValue="value" showClear filter></Dropdown>

      <label>Corrected packaging</label>
      <Dropdown v-model="correctedPackagingId" :options="packagingOptions" optionLabel="label" optionValue="value" showClear filter></Dropdown>

      <label>Note</label>
      <InputText v-model="correctionNote"></InputText>
    </div>

    <template #footer>
      <Button :label="translations.generic.cancel" text @click="visible = false"></Button>
      <Button :label="translations.generic.save" :loading="loading" @click="save"></Button>
    </template>
  </Dialog>
</template>

<style scoped>
.form-grid {
  display: grid;
  grid-template-columns: 11rem 1fr;
  gap: 0.75rem;
  align-items: center;
}
</style>
